import React, { useState, useEffect } from 'react';
import { FONT, TEXT, TEXT2, TEXT3, BLUE, BORDER, SUCCESS, useIsMobile } from '../shared';

// Flip CHROME_STORE_URL once the Chrome Web Store listing is approved.
// While empty → "Manual install" section auto-opens.
const CHROME_STORE_URL = '';

// Internal beta: extension is served by the backend at
// /api/relay/extension.zip (authed). The download is a fetch+blob flow
// rather than a plain <a href> because the JWT goes in an Authorization
// header, not a query param.
const API_BASE = process.env.REACT_APP_API_URL || 'https://vella-backend-production.up.railway.app';
const EXTENSION_DOWNLOAD_URL = `${API_BASE}/api/relay/extension.zip`;

const CARD_BASE = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.90) 0%, rgba(255,255,255,0.78) 100%)",
  backdropFilter: "blur(34px) saturate(180%)",
  WebkitBackdropFilter: "blur(34px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.65)",
  borderRadius: 24,
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.95), 0 18px 44px -18px rgba(11,107,211,0.22), 0 8px 24px -10px rgba(14,165,233,0.14)",
  maxWidth: 520,
  width: "100%",
};

export default function RelayAuth() {
  const isMobile = useIsMobile();
  const CARD = { ...CARD_BASE, padding: isMobile ? "26px 20px" : "40px 44px", borderRadius: isMobile ? 20 : 24 };
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('vella_token') || '';
    setToken(t);
  }, []);

  const handleCopy = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // The endpoint requires Authorization: Bearer <jwt>, which a plain <a href>
  // can't send. So fetch the zip as a blob, materialize an object URL, and
  // synthesize a click on a hidden link to trigger the browser's "Save As".
  const handleDownload = async () => {
    if (!token) {
      setDownloadError('Please sign in first to download the extension.');
      return;
    }
    setDownloading(true);
    setDownloadError('');
    try {
      const res = await fetch(EXTENSION_DOWNLOAD_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(res.status === 401 ? 'Session expired — sign in again.' : `Download failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vella-relay-extension.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after the click has had a chance to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setDownloadError(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontFamily: FONT, padding: isMobile ? '12px 4px' : 0, boxSizing: 'border-box' }}>
      <div style={CARD}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: `linear-gradient(135deg, #38BDF8, ${BLUE} 60%, #0284C7)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: '-0.022em' }}>Connect Vella Relay</h2>
            <p style={{ fontSize: 13, color: TEXT2, margin: '2px 0 0' }}>Authenticate the Chrome extension</p>
          </div>
        </div>

        {/* Token field */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: 'uppercase', letterSpacing: 1.2, display: 'block', marginBottom: 8 }}>
            Your Access Token
          </label>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
            <input
              readOnly
              value={token || 'Not logged in — please sign in first'}
              style={{
                flex: 1,
                width: isMobile ? '100%' : undefined,
                padding: '11px 14px',
                borderRadius: 12,
                border: `1px solid ${BORDER}`,
                background: 'rgba(241,245,249,0.85)',
                fontSize: 12,
                color: token ? TEXT : TEXT3,
                fontFamily: 'monospace',
                outline: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={handleCopy}
              disabled={!token}
              style={{
                padding: '11px 18px',
                borderRadius: 12,
                border: 'none',
                background: copied ? SUCCESS : `linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)`,
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: token ? 'pointer' : 'not-allowed',
                opacity: token ? 1 : 0.4,
                fontFamily: FONT,
                flexShrink: 0,
                transition: 'background 0.2s',
                whiteSpace: 'nowrap',
                minHeight: 44,
                width: isMobile ? '100%' : undefined,
              }}
            >
              {copied ? '✓ Copied' : 'Copy Token'}
            </button>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${BORDER}, transparent)`, margin: '24px 0' }} />

        {/* Install — Chrome Web Store (preferred, once approved) */}
        <div style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
            Install — Recommended
          </p>
          <a
            href={CHROME_STORE_URL || '#'}
            onClick={(e) => { if (!CHROME_STORE_URL) e.preventDefault(); }}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '13px 20px',
              borderRadius: 14,
              background: CHROME_STORE_URL
                ? `linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)`
                : 'rgba(15,23,42,0.06)',
              border: 'none',
              color: CHROME_STORE_URL ? '#fff' : TEXT3,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              fontFamily: FONT,
              boxSizing: 'border-box',
              cursor: CHROME_STORE_URL ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="4"/>
              <line x1="21.17" y1="8" x2="12" y2="8"/>
              <line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
              <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
            </svg>
            {CHROME_STORE_URL ? 'Add to Chrome from the Web Store' : 'Chrome Web Store — pending review'}
          </a>
          {!CHROME_STORE_URL && (
            <p style={{ fontSize: 11, color: TEXT3, margin: '8px 2px 0', lineHeight: 1.5 }}>
              The Vella Relay listing is in review. While we wait, you can install
              manually below — it's the same extension.
            </p>
          )}
        </div>

        {/* Install — Manual / Developer mode (fallback while CWS pending) */}
        <details style={{ marginBottom: 6 }} open={!CHROME_STORE_URL}>
          <summary style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14, cursor: 'pointer', listStyle: 'none' }}>
            Install — Manual (Developer mode) ▾
          </summary>
          <div style={{ marginTop: 14, marginBottom: 16 }}>
            {[
              EXTENSION_DOWNLOAD_URL
                ? 'Click "Download Extension" below to grab the .zip, then unzip it on your computer.'
                : (
                  <>
                    Get the <code style={{ background: 'rgba(15,23,42,0.06)', padding: '1px 6px', borderRadius: 6, fontSize: 12 }}>vella-relay-extension</code> folder
                    from the team — it lives in the Vella backend repo and isn't publicly distributable yet
                    (Chrome Web Store listing in review).
                  </>
                ),
              <>Open <code style={{ background: 'rgba(15,23,42,0.06)', padding: '1px 6px', borderRadius: 6, fontSize: 12 }}>chrome://extensions</code> in a new browser tab.</>,
              'Enable "Developer mode" using the toggle in the top-right corner.',
              <>Click <strong>Load unpacked</strong> and select the <code style={{ background: 'rgba(15,23,42,0.06)', padding: '1px 6px', borderRadius: 6, fontSize: 12 }}>vella-relay-extension</code> folder.</>,
              'Click the Vella Relay icon in your Chrome toolbar (puzzle piece menu if it\'s hidden).',
              'Paste your access token above into the extension\'s field and click Connect.',
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: `rgba(59,130,246,0.12)`, border: `1px solid rgba(59,130,246,0.25)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: BLUE }}>{i + 1}</span>
                </div>
                <p style={{ fontSize: 13, color: TEXT2, margin: 0, lineHeight: 1.55 }}>{step}</p>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleDownload}
            disabled={!token || downloading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '12px 20px',
              borderRadius: 14,
              background: 'rgba(255,255,255,0.72)',
              border: '1px solid rgba(180,205,235,0.55)',
              backdropFilter: 'blur(22px) saturate(180%)',
              WebkitBackdropFilter: 'blur(22px) saturate(180%)',
              color: BLUE,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: FONT,
              cursor: !token || downloading ? 'not-allowed' : 'pointer',
              opacity: !token || downloading ? 0.6 : 1,
              boxSizing: 'border-box',
              transition: 'all 0.2s',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {downloading ? 'Downloading…' : 'Download Extension (.zip)'}
          </button>
          {downloadError && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#B91C1C' }}>
              {downloadError}
            </div>
          )}
        </details>
      </div>
    </div>
  );
}
